import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../utils/api';
import { toast } from 'sonner';
import { Calendar, Clock, Mail, Send, Plus, CheckCircle, XCircle, AlertCircle, Trash } from 'lucide-react';

const emailTypes = [
  { value: '24h_reminder', label: '24-Hour Reminder', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  { value: '1h_reminder', label: '1-Hour Reminder', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'post_appointment', label: 'Post-Appointment Follow-up', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  { value: 'no_show', label: 'No-Show Follow-up', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' }
];

const statusColors = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  no_show: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-gray-100 text-gray-700'
};

export const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prospects, setProspects] = useState([]);
  
  const [formData, setFormData] = useState({
    prospect_id: '',
    prospect_name: '',
    prospect_email: '',
    appointment_datetime: '',
    notes: ''
  });

  useEffect(() => {
    fetchAppointments();
    fetchProspects();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments');
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    }
  };

  const fetchProspects = async () => {
    try {
      const response = await api.get('/prospects');
      setProspects(response.data);
    } catch (error) {
      // Silent fail - prospects dropdown will be empty
    }
  };

  const handleProspectSelect = (prospectId) => {
    const prospect = prospects.find(p => p.id === prospectId);
    if (prospect) {
      setFormData({
        ...formData,
        prospect_id: prospect.id,
        prospect_name: prospect.name,
        prospect_email: ''
      });
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!formData.prospect_email) {
      toast.error('Prospect email is required');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/appointments', formData);
      toast.success('Appointment created successfully!');
      setShowCreateModal(false);
      setFormData({
        prospect_id: '',
        prospect_name: '',
        prospect_email: '',
        appointment_datetime: '',
        notes: ''
      });
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (emailType) => {
    if (!selectedAppointment) return;
    
    setLoading(true);
    try {
      await api.post('/appointments/send-email', {
        appointment_id: selectedAppointment.id,
        email_type: emailType
      });
      toast.success('Email sent successfully!');
      setShowEmailModal(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await api.put(`/appointments/${appointmentId}/status?status=${status}`);
      toast.success('Status updated!');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await api.delete(`/appointments/${appointmentId}`);
      toast.success('Appointment deleted!');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const upcomingAppointments = appointments.filter(a => 
    a.status === 'scheduled' && new Date(a.appointment_datetime) >= new Date()
  );
  
  const pastAppointments = appointments.filter(a => 
    a.status !== 'scheduled' || new Date(a.appointment_datetime) < new Date()
  );

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Appointments & Follow-ups</h1>
          <p className="text-gray-600 mt-1">Manage appointments and send follow-up emails</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
          data-testid="create-appointment-button"
        >
          <Plus className="h-4 w-4" />
          New Appointment
        </Button>
      </div>

      {/* Email Types Legend */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">Available Email Templates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {emailTypes.map(type => {
            const Icon = type.icon;
            return (
              <div key={type.value} className={`p-3 rounded-lg ${type.color} flex items-center gap-2`}>
                <Icon className="h-4 w-4" />
                <span className="text-sm font-medium">{type.label}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Upcoming Appointments */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-[#E31837]" />
          Upcoming Appointments ({upcomingAppointments.length})
        </h2>
        
        {upcomingAppointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No upcoming appointments</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prospect</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Emails Sent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingAppointments.map(appointment => (
                  <TableRow key={appointment.id} data-testid={`appointment-row-${appointment.id}`}>
                    <TableCell className="font-medium">{appointment.prospect_name}</TableCell>
                    <TableCell>{formatDateTime(appointment.appointment_datetime)}</TableCell>
                    <TableCell className="text-sm text-gray-600">{appointment.prospect_email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {appointment.emails_sent?.map((email, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {email.type.replace('_', ' ')}
                          </Badge>
                        ))}
                        {(!appointment.emails_sent || appointment.emails_sent.length === 0) && (
                          <span className="text-xs text-gray-400">None sent</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAppointment(appointment);
                            setShowEmailModal(true);
                          }}
                          className="flex items-center gap-1"
                          data-testid={`send-email-${appointment.id}`}
                        >
                          <Send className="h-3 w-3" />
                          Send Email
                        </Button>
                        <Select onValueChange={(status) => handleUpdateStatus(appointment.id, status)}>
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue placeholder="Update" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="no_show">No Show</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Past Appointments */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-500" />
          Past Appointments ({pastAppointments.length})
        </h2>
        
        {pastAppointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No past appointments</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prospect</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Emails Sent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastAppointments.map(appointment => (
                  <TableRow key={appointment.id} className="opacity-75">
                    <TableCell className="font-medium">{appointment.prospect_name}</TableCell>
                    <TableCell>{formatDateTime(appointment.appointment_datetime)}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[appointment.status]}>
                        {appointment.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {appointment.emails_sent?.map((email, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {email.type.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {(appointment.status === 'no_show' || appointment.status === 'completed') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedAppointment(appointment);
                              setShowEmailModal(true);
                            }}
                            className="flex items-center gap-1"
                          >
                            <Send className="h-3 w-3" />
                            Follow-up
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAppointment(appointment.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create Appointment Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-appointment-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Appointment</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateAppointment} className="space-y-4 mt-4">
            <div>
              <Label>Select Prospect (Optional)</Label>
              <Select onValueChange={handleProspectSelect}>
                <SelectTrigger data-testid="prospect-select">
                  <SelectValue placeholder="Choose from existing prospects" />
                </SelectTrigger>
                <SelectContent>
                  {prospects.map(prospect => (
                    <SelectItem key={prospect.id} value={prospect.id}>
                      {prospect.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prospect_name">Prospect Name *</Label>
              <Input
                id="prospect_name"
                value={formData.prospect_name}
                onChange={(e) => setFormData({ ...formData, prospect_name: e.target.value })}
                required
                data-testid="prospect-name-input"
              />
            </div>

            <div>
              <Label htmlFor="prospect_email">Prospect Email *</Label>
              <Input
                id="prospect_email"
                type="email"
                value={formData.prospect_email}
                onChange={(e) => setFormData({ ...formData, prospect_email: e.target.value })}
                required
                placeholder="email@example.com"
                data-testid="prospect-email-input"
              />
            </div>

            <div>
              <Label htmlFor="appointment_datetime">Appointment Date & Time *</Label>
              <Input
                id="appointment_datetime"
                type="datetime-local"
                value={formData.appointment_datetime}
                onChange={(e) => setFormData({ ...formData, appointment_datetime: e.target.value })}
                required
                data-testid="appointment-datetime-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                placeholder="Any additional notes..."
                data-testid="appointment-notes-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                disabled={loading}
                data-testid="submit-appointment-button"
              >
                {loading ? 'Creating...' : 'Create Appointment'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent data-testid="send-email-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Email to {selectedAppointment?.prospect_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Sending to: <strong>{selectedAppointment?.prospect_email}</strong>
            </p>
            
            <div className="text-sm text-gray-500 mb-4">
              Appointment: {selectedAppointment && formatDateTime(selectedAppointment.appointment_datetime)}
            </div>

            <div className="space-y-3">
              {emailTypes.map(type => {
                const Icon = type.icon;
                const alreadySent = selectedAppointment?.emails_sent?.some(e => e.type === type.value);
                
                return (
                  <Button
                    key={type.value}
                    variant="outline"
                    className={`w-full justify-start ${type.color} border-2`}
                    onClick={() => handleSendEmail(type.value)}
                    disabled={loading}
                    data-testid={`send-${type.value}-button`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {type.label}
                    {alreadySent && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        Sent
                      </Badge>
                    )}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              onClick={() => setShowEmailModal(false)}
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
